import { useContext, useEffect, useRef, useState } from 'react'
import styles from './PPTDownloadBtn.module.less'
import { createPortal } from 'react-dom'
import PPTBox from './PPTBox/PPTBox'
import { doPPTExport } from './PPTBox/doPPTExport'
import { MessageContext } from '@/Contexts/Contexts'
import { SliderModels } from '@/types/slider'
import { useTranslation } from 'react-i18next'
import NiceModal from '@ebay/nice-modal-react'
import DownloadPptRechargeModal from '../Modals/DownloadPptRechargeModal/DownloadPptRechargeModal'

const PPT_PROCESSING_MESSAGE_KEY = 'ppt-processing'

interface Props {
  as: (p: { loading: boolean }) => React.ReactNode
  project: ProjectModels.ProjectDetail
  debug?: boolean
  hasTranslated?: boolean
}

function PPTDownloadBtn(props: Props) {
  const { t } = useTranslation()
  const { message } = useContext(MessageContext)
  const [startDownload, setStartDownload] = useState(false)
  const sliders = useRef<SliderModels.Slider[]>([])
  const [loading, setLoading] = useState(false)
  const downloadingRef = useRef(false)
  const exportingRef = useRef(false)
  const finishToastTimerRef = useRef<number>()

  useEffect(() => {
    return () => {
      message.destroy(PPT_PROCESSING_MESSAGE_KEY)
      if (finishToastTimerRef.current) {
        window.clearTimeout(finishToastTimerRef.current)
      }
      downloadingRef.current = false
      exportingRef.current = false
    }
  }, [message])

  const handleDownload = () => {
    if (downloadingRef.current || loading || startDownload) {
      return
    }
    if (props.project.isFreeTrial) {
      NiceModal.show(DownloadPptRechargeModal)
      return
    }
    if (props.project.status !== 'completed') {
      return
    }
    if (props.debug) {
      downloadingRef.current = true
      message.warning('当前处于 debug 默认，仅能调试 html ，不进行打印')
      setStartDownload(true)
      setLoading(true)
      return
    }

    if (!props.hasTranslated) {
      message.warning(t('dashboard.ppt.pptProcessingDesc2'))
      return
    }

    downloadingRef.current = true
    setStartDownload(true)
    setLoading(true)
    message.open({
      key: PPT_PROCESSING_MESSAGE_KEY,
      type: 'loading',
      content: t('dashboard.ppt.pptProcessingDesc'),
      duration: 0
    })

  }

  const handleProgress = (total: number, percent: number) => {
    if (props.debug) {
      message.open({
        key: 'ppt-debug',
        type: 'loading',
        content: `总数${total}，当前${percent}%`,
      })
    }
    // else {
    //   message.open({
    //     key: 'ppt-processing',
    //     type: 'loading',
    //     content: t('dashboard.ppt.pptProcessingDesc'),
    //   })
    // }
  }

  const handleFinished = async (s: SliderModels.Slider[]) => {
    if (exportingRef.current) {
      return
    }
    exportingRef.current = true
    if (props.debug) {
      message.destroy()
      downloadingRef.current = false
      exportingRef.current = false
      setStartDownload(false)
      setLoading(false)
      return
    }
    sliders.current = s
    console.log('sliders', sliders.current)
    let success = false
    try {
      await doPPTExport(sliders.current, props.project.projectName)
      success = true
    } catch (error) {
      console.log('error', error)
    }
    exportingRef.current = false
    setStartDownload(false)
    finishToastTimerRef.current = window.setTimeout(() => {
      /**
       * 进行到这里，已经处于浏览器下载文件的时间，不太可控，
       * 而且这里可能因为下载文件比较慢，toast 消失后，用户感
       * 官上会有一个视觉等待，因此略微延缓 toast 消失的时机，
       * 减少用户感官上的视觉等待。
       */
      message.destroy()
      if (success) {
        message.success(t('dashboard.ppt.exportSuccess'))
      } else {
        message.warning(t('dashboard.ppt.exportFailed'))
      }
      downloadingRef.current = false
      setLoading(false)
      finishToastTimerRef.current = undefined
    }, 1500)
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span className={styles.pptDownloadBtn} onClick={handleDownload} data-testid='ppt-download-btn'>
          {props.as({ loading })}
        </span>
      </div>
      {startDownload &&
        createPortal(<PPTBox project={props.project} debug={props.debug} onProgress={handleProgress} onFinished={handleFinished} />, document.body)}
    </>
  )
}

export default PPTDownloadBtn
